import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { FaEnvelope, FaGlobe, FaPhone } from "react-icons/fa6";
import { useAccountInfo, useStaticPages } from "../../api/queryHooks/account";
import { useConsent } from "../../consent/useConsent";
import MarkdownContent from "../common/MarkdownContent";
import PaymentProviderBanner from "../order/PaymentProviderBanner";
import { displayUrl } from "../../utils/displayUrl";
import "./Footer.scss";

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { data: accountInfo } = useAccountInfo();
  const { data: staticPages } = useStaticPages();
  const { hasConsentableServices, openPreferences } = useConsent();
  const barionPixelId = accountInfo?.setting?.barion_pixel_id;
  // Whitelabel switch (Shop basics → "Powered by Foto2Me" credit). The backend
  // only serializes it for whitelabel-enabled accounts, so a shop without the
  // feature can never switch the credit off. Shop mail honours the same flag
  // (shop_mailer.rb).
  const hidePlatformBranding = accountInfo?.setting?.hide_platform_branding === true;
  // The copyright credits the shop display name (Shop basics → Shop name),
  // falling back to the account name.
  const copyrightHolder = accountInfo?.display_name || accountInfo?.name || 'Foto2Me';
  // tel: wants the dial string only; the visible text keeps the grouping.
  const telHref = accountInfo?.contact_phone ? `tel:${accountInfo.contact_phone.replace(/[^\d+]/g, '')}` : null;

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h4 className="footer-title">{t('footer.about_title')}</h4>
            {/* Admin-authored markdown (Admin → Pages → footer content). No
                platform default: a photographer's shop must not advertise
                Foto2Me in its own About column. */}
            {accountInfo?.setting?.footer_content && (
              <MarkdownContent content={accountInfo.setting.footer_content} />
            )}
            {accountInfo?.website_url && (
              <div className="footer-links">
                <a href={accountInfo.website_url} target="_blank" rel="noopener noreferrer" className="footer-link">
                  <span className="icon is-small">
                    <FaGlobe />
                  </span>
                  <span>{displayUrl(accountInfo.website_url)}</span>
                </a>
              </div>
            )}
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.contact_title')}</h4>
            <div className="footer-links">
              {accountInfo?.contact_email && (
                <a href={`mailto:${accountInfo.contact_email}`} className="footer-link">
                  <span className="icon is-small">
                    <FaEnvelope />
                  </span>
                  <span>{accountInfo.contact_email}</span>
                </a>
              )}
              {telHref && (
                <a href={telHref} className="footer-link">
                  <span className="icon is-small">
                    <FaPhone />
                  </span>
                  <span>{accountInfo?.contact_phone}</span>
                </a>
              )}
            </div>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.links_title')}</h4>
            <div className="footer-links">
              {staticPages?.filter((page) => page.show_in_footer !== false).map((page) => (
                <a key={page.id} href={`/page/${page.slug}`} className="footer-link">{page.title}</a>
              ))}
              {/* Foto2Me-owned cookie information (see docs/CONSENT.md); always present. */}
              <Link to="/cookies" className="footer-link">{t('footer.cookies')}</Link>
              {hasConsentableServices && (
                <button type="button" className="footer-link footer-link-button" onClick={openPreferences}>
                  {t('footer.cookie_settings')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Barion's "main page" logo-strip requirement: the footer is on every page. */}
        {barionPixelId && <PaymentProviderBanner techid="barion" className="footer-payment-strip is-compact" />}

        <div className="footer-bottom">
          <div className="footer-copyright">
            <p>© {currentYear} {copyrightHolder}. {t('footer.rights_reserved')}</p>
          </div>
          {!hidePlatformBranding && (
            <div className="footer-powered">
              <p>
                {t('footer.powered_by')} <a href="https://foto2.me" target="_blank" rel="noopener noreferrer" className="footer-brand">Foto2Me</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
